import React, { useState, useMemo } from "react";
import {
  DEMAND_PLAN_VS_ACTUAL, SKU_LIFECYCLE, PRICE_ELASTICITY, PROMO_CALENDAR,
  SEASONALITY_WEATHER, BRAND_CATEGORY_MARGIN, NPI_TRACKER,
} from "../lib/dataExtras";
import { SUMMER_PRESEASON, SUMMER_BRANCH_GAPS } from "../lib/dataUseCases";
import { CATEGORIES, KPI_SNAPSHOT } from "../lib/data";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  ComposedChart, Line, Legend, Cell,
} from "recharts";
import { useChartColors } from "../lib/chartColors";
import { useCopilot } from "../lib/copilot.jsx";
import { Sparkles, MessageSquare, Sun, AlertTriangle } from "lucide-react";

const tickStyle = { fill: "rgb(var(--muted))", fontSize: 10, fontFamily: "IBM Plex Mono" };

function TT({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border-strong px-3 py-2 text-xs rounded-sm shadow-lg max-w-xs">
      {label && <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 font-mono-tabular">
          <span className="w-2 h-2" style={{ background: p.color || p.payload?.fill }} />
          <span className="text-muted">{p.name}</span>
          <span className="ml-auto font-semibold">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

const HEAT = (varPct) => {
  if (varPct > 0.20) return "rgb(var(--success) / 0.85)";
  if (varPct > 0.05) return "rgb(var(--success) / 0.45)";
  if (varPct > -0.05) return "rgb(var(--surface-2))";
  if (varPct > -0.20) return "rgb(var(--critical) / 0.45)";
  return "rgb(var(--critical) / 0.85)";
};

const LIFECYCLE_COLORS = { Intro: "var(--info)", Growth: "var(--success)", Mature: "var(--primary)", Decline: "var(--warning)" };

export default function Merchandising() {
  const C = useChartColors();
  const { askCopilot } = useCopilot();
  const [activePromo, setActivePromo] = useState(PROMO_CALENDAR[0]);
  const [discountAdj, setDiscountAdj] = useState(activePromo.discount * 100);

  // Promo uplift sim
  const promoSim = useMemo(() => {
    const baseUnits = activePromo.skus * 38;
    const elasticity = -1.6;
    const factor = 1 + Math.pow(discountAdj / (activePromo.discount * 100), 1.3) * (activePromo.expectedUplift - 1);
    const uplift = +(factor).toFixed(2);
    const cannib = +(activePromo.cannib * (discountAdj / (activePromo.discount * 100))).toFixed(2);
    const orders = Math.round(baseUnits * uplift);
    const grossRev = Math.round(orders * 1450 * (1 - discountAdj / 100));
    const netImpact = Math.round(grossRev - orders * cannib * 1450 * 0.4);
    const marginImpact = -(discountAdj / 100) * 0.6;
    return { uplift, cannib, orders, grossRev, netImpact, marginImpact };
  }, [activePromo, discountAdj]);

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART III + V · Merchandising" title="Merchandising & Demand Planning"
        description="Demand plan vs actuals, SKU lifecycle stages, price elasticity, promotion uplift simulator and weather-driven seasonality." />

      <div className="tile grid grid-cols-2 md:grid-cols-6 divide-x divide-border">
        <Agg label="Active Plans" value={DEMAND_PLAN_VS_ACTUAL.length} />
        <Agg label="Plan Accuracy" value={`${Math.round(DEMAND_PLAN_VS_ACTUAL.reduce((s, d) => s + d.accuracy, 0) / DEMAND_PLAN_VS_ACTUAL.length * 100)}%`} status="success" />
        <Agg label="Promotions Pipeline" value={PROMO_CALENDAR.filter(p => p.status === "scheduled").length} status="info" />
        <Agg label="NPI Pipeline" value={NPI_TRACKER.length} status="info" />
        <Agg label="At-Risk NPIs" value={NPI_TRACKER.filter(n => n.status === "at-risk" || n.status === "blocked").length} status="warning" />
        <Agg label="Mature SKUs" value={SKU_LIFECYCLE.counts.find(c => c.stage === "Mature").count} status="success" />
      </div>

      <div className="grid grid-cols-12 gap-6">

        {/* Summer Pre-Season Planning */}
        <Tile className="col-span-12" testid="summer-preseason"
              title="Summer Pre-Season Planning — Top Critical SKUs"
              action={<div className="flex items-center gap-2">
                <Badge status="critical">{SUMMER_PRESEASON.filter(s => s.risk === "critical").length} critical gaps</Badge>
                <Badge status="warning">{formatPeso(SUMMER_PRESEASON.reduce((s, x) => s + x.investment, 0), { compact: true })} investment</Badge>
                <button
                  data-testid="ask-copilot-summer"
                  onClick={() => askCopilot(
                    `As Merchandising Manager, I'm planning summer pre-season stock. The AI flagged ${SUMMER_PRESEASON.length} summer-critical SKUs with ${SUMMER_PRESEASON.filter(s => s.risk === "critical").length} in critical gap status, total investment required ${formatPeso(SUMMER_PRESEASON.reduce((s, x) => s + x.investment, 0), { compact: true })}. The top-5 gaps: ${SUMMER_PRESEASON.slice(0, 5).map(s => `${s.name} (gap ${s.gapUnits}u across ${s.branchesShort} branches, lead-time ${s.leadTimeDays}d, investment ${formatPeso(s.investment, { compact: true })})`).join("; ")}. PAGASA forecasts a hotter-than-average summer with 42°C heat-index in NCR. Advise: (1) which SKUs should I prioritise for emergency air-freight vs sea-freight? (2) which branches in Metro Manila + South Luzon need pre-position first given the 6-week window? (3) how do I sequence the import POs to stay within working capital limits (current CCC 29 days)?`
                  )}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"
                >
                  <MessageSquare size={11} /> Ask Copilot →
                </button>
              </div>}>
          <div className="max-h-[360px] overflow-y-auto -mx-4">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-surface z-10">
                <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                  <th className="py-2 px-3 text-left">SKU</th>
                  <th className="py-2 px-3 text-right">Forecast</th>
                  <th className="py-2 px-3 text-right">On Hand</th>
                  <th className="py-2 px-3 text-right">Gap</th>
                  <th className="py-2 px-3 text-right">Branches Short</th>
                  <th className="py-2 px-3 text-right">Lead Time</th>
                  <th className="py-2 px-3 text-right">Order By</th>
                  <th className="py-2 px-3 text-right">Investment ₱</th>
                  <th className="py-2 px-3 text-left">Risk</th>
                </tr>
              </thead>
              <tbody className="font-mono-tabular">
                {SUMMER_PRESEASON.map(s => (
                  <tr key={s.skuId} data-testid={`summer-row-${s.skuId}`} className="border-b border-border hover:bg-surface-2">
                    <td className="py-2 px-3 font-sans">
                      <div className="text-sm font-medium">{s.name}</div>
                      <div className="text-[10px] text-muted">{s.skuId} · {s.abc}-class</div>
                    </td>
                    <td className="py-2 px-3 text-right">{formatNumber(s.forecastUnits)}</td>
                    <td className="py-2 px-3 text-right text-muted">{formatNumber(s.onHandUnits)}</td>
                    <td className={cn("py-2 px-3 text-right font-semibold", s.risk === "critical" ? "text-critical" : s.risk === "warning" ? "text-warning" : "text-fg")}>
                      {formatNumber(s.gapUnits)}
                    </td>
                    <td className="py-2 px-3 text-right">{s.branchesShort}</td>
                    <td className="py-2 px-3 text-right">{s.leadTimeDays}d</td>
                    <td className={cn("py-2 px-3 text-right", s.orderByDays > 60 ? "text-warning" : "text-fg")}>D+{s.orderByDays}</td>
                    <td className="py-2 px-3 text-right">{formatPeso(s.investment, { compact: true })}</td>
                    <td className="py-2 px-3 font-sans"><Badge status={s.risk}>{s.risk}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {SUMMER_BRANCH_GAPS.slice(0, 4).map(b => (
              <div key={b.branchId} data-testid={`summer-cover-${b.branchId}`} className="p-2 border border-border rounded-sm bg-surface-2/40">
                <div className="text-[10px] text-muted">{b.region}</div>
                <div className="text-sm font-medium">{b.name}</div>
                <div className="mt-1 flex items-center justify-between text-[11px] font-mono-tabular">
                  <span className="text-muted">{b.coverWeeks}w cover</span>
                  <span className="text-warning">{b.skusShort} SKUs short</span>
                </div>
                <div className="text-[10px] text-primary mt-0.5">{formatPeso(b.investmentNeeded, { compact: true })} needed</div>
              </div>
            ))}
          </div>
        </Tile>

        {/* Demand Plan vs Actual heatmap */}
        <Tile className="col-span-12" testid="demand-plan-heatmap"
              title="Demand Plan vs Actual Heatmap — A-Class SKUs × Last 12 Weeks"
              action={<div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: HEAT(-0.25) }} />Under-plan</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: HEAT(0) }} />On-plan</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3" style={{ background: HEAT(0.25) }} />Over-plan</span>
              </div>}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead>
                <tr>
                  <th className="text-left text-muted uppercase tracking-widest font-semibold py-2 pr-3 sticky left-0 bg-surface">SKU</th>
                  {DEMAND_PLAN_VS_ACTUAL[0].weeks.map(w => (
                    <th key={w.week} className="px-1 text-muted font-mono-tabular text-[10px]">{w.week}</th>
                  ))}
                  <th className="px-2 text-muted text-[10px] uppercase tracking-widest text-right">Acc.</th>
                </tr>
              </thead>
              <tbody>
                {DEMAND_PLAN_VS_ACTUAL.map(row => (
                  <tr key={row.skuId}>
                    <td className="py-1 pr-3 sticky left-0 bg-surface" style={{ maxWidth: 220 }}>
                      <div className="text-[11px] font-medium truncate">{row.name}</div>
                      <div className="text-[9px] text-muted font-mono-tabular">{row.skuId} · {row.category}</div>
                    </td>
                    {row.weeks.map((w, i) => (
                      <td key={i} className="p-0.5">
                        <div className="w-8 h-7 flex items-center justify-center font-mono-tabular text-[9px] font-semibold rounded-sm"
                             title={`Plan ${w.plan} · Actual ${w.actual} · ${w.varPct >= 0 ? "+" : ""}${(w.varPct * 100).toFixed(0)}%`}
                             style={{ background: HEAT(w.varPct), color: Math.abs(w.varPct) > 0.20 ? "var(--primary-fg)" : "var(--fg)" }}>
                          {w.varPct >= 0 ? "+" : ""}{Math.round(w.varPct * 100)}
                        </div>
                      </td>
                    ))}
                    <td className={cn("text-right px-2 font-mono-tabular font-semibold", row.accuracy >= 0.85 ? "text-success" : row.accuracy >= 0.7 ? "text-warning" : "text-critical")}>
                      {(row.accuracy * 100).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-muted mt-3 leading-relaxed">
            <strong className="text-fg">AI insight:</strong> Bias toward under-planning on Freon R32 and Inverter Compressors. Recommend recalibrating baseline forecast with +18% summer multiplier and PAGASA heat-index linkage.
          </div>
        </Tile>

        {/* SKU Lifecycle */}
        <Tile className="col-span-12 lg:col-span-5" testid="sku-lifecycle"
              title="SKU Lifecycle Stages"
              action={<Badge status="info">{SKU_LIFECYCLE.dist.length} SKUs tracked</Badge>}>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {SKU_LIFECYCLE.counts.map(c => (
              <div key={c.stage} className="text-center p-3 rounded-sm border border-border" style={{ background: `${c.color.replace("var(--", "rgb(var(--").replace(")", "") + ") / 0.08)"}` }}>
                <div className="text-[9px] uppercase tracking-widest text-muted">{c.stage}</div>
                <div className="text-2xl font-display font-semibold mt-1" style={{ color: c.color }}>{c.count}</div>
                <div className="text-[10px] text-muted font-mono-tabular">{formatNumber(c.units, { compact: true })} u/mo</div>
              </div>
            ))}
          </div>
          <div className="max-h-56 overflow-y-auto -mx-4">
            <div className="space-y-0 divide-y divide-border">
              {SKU_LIFECYCLE.dist.slice(0, 14).map(s => (
                <div key={s.skuId} className="px-4 py-2 flex items-center gap-2 text-xs" data-testid={`lifecycle-${s.skuId}`}>
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LIFECYCLE_COLORS[s.stage] }} />
                  <div className="flex-1 truncate">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-muted ml-2">{s.stage}</span>
                  </div>
                  <span className="font-mono-tabular text-muted text-[10px]">{formatNumber(s.monthlyUnits)} u</span>
                </div>
              ))}
            </div>
          </div>
        </Tile>

        {/* Price Elasticity */}
        <Tile className="col-span-12 lg:col-span-7" testid="price-elasticity"
              title="Price Elasticity by Category"
              action={<Badge status="info">% chg in qty per % chg in price</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">Category</th>
                <th className="py-2 px-3 text-right">Elasticity (β)</th>
                <th className="py-2 px-3 text-left">Sensitivity</th>
                <th className="py-2 px-3 text-right">Optimal Δ</th>
                <th className="py-2 px-3 text-right">Revenue Impact</th>
                <th className="py-2 px-3 text-left">Recommendation</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {PRICE_ELASTICITY.map(e => (
                <tr key={e.categoryId} className="border-b border-border" data-testid={`elasticity-${e.categoryId}`}>
                  <td className="py-3 px-3 font-sans font-medium">{e.category}</td>
                  <td className="py-3 px-3 text-right font-semibold">{e.elasticity}</td>
                  <td className="py-3 px-3 font-sans">
                    <Badge status={e.elasticity < -1.5 ? "critical" : e.elasticity < -0.8 ? "warning" : "success"}>{e.bucket}</Badge>
                  </td>
                  <td className={cn("py-3 px-3 text-right", e.optimalAdjPct > 0 ? "text-success" : "text-critical")}>
                    {e.optimalAdjPct >= 0 ? "+" : ""}{(e.optimalAdjPct * 100).toFixed(1)}%
                  </td>
                  <td className={cn("py-3 px-3 text-right font-semibold", e.revenueImpact > 0 ? "text-success" : "text-critical")}>
                    {e.revenueImpact >= 0 ? "+" : ""}{formatPeso(e.revenueImpact, { compact: true })}/mo
                  </td>
                  <td className="py-3 px-3 font-sans text-[11px] text-muted">
                    {e.elasticity < -1.5 ? "Hold price; rely on volume" : e.elasticity < -0.8 ? "Test ±5% A/B" : "Raise price; demand sticky"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Tile>

        {/* Promotion Uplift Simulator */}
        <Tile className="col-span-12 lg:col-span-7" testid="promo-simulator"
              title="Promotion Uplift Simulator"
              action={<Badge status="success">AI · Cannibalization-aware</Badge>}>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <div className="kpi-label mb-2">Select promotion</div>
              <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                {PROMO_CALENDAR.map(p => (
                  <button key={p.name} data-testid={`promo-${p.week}`}
                          onClick={() => { setActivePromo(p); setDiscountAdj(p.discount * 100); }}
                          className={cn("w-full text-left p-2.5 border rounded-sm transition-colors",
                            activePromo.name === p.name ? "border-primary bg-primary/5" : "border-border bg-surface-2/40 hover:bg-surface-2")}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-muted font-mono-tabular">{p.week}</span>
                      <Badge status={p.status === "scheduled" ? "success" : "neutral"}>{p.status}</Badge>
                    </div>
                    <div className="text-sm font-medium mt-0.5 truncate">{p.name}</div>
                    <div className="text-[10px] text-muted mt-0.5">{p.channel} · {p.skus} SKUs</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-12 md:col-span-8 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="kpi-label">Discount Level</label>
                  <span className="font-mono-tabular text-sm font-semibold text-primary">{discountAdj.toFixed(0)}%</span>
                </div>
                <input type="range" min="5" max="50" value={discountAdj}
                       data-testid="promo-discount-slider"
                       onChange={e => setDiscountAdj(parseInt(e.target.value, 10))}
                       className="w-full accent-primary" />
                <div className="flex justify-between text-[10px] text-subtle font-mono-tabular mt-1">
                  <span>5%</span><span>Baseline {(activePromo.discount * 100).toFixed(0)}%</span><span>50%</span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <SimStat label="Demand Uplift" value={`${promoSim.uplift.toFixed(2)}×`} tone="success" />
                <SimStat label="Cannibalization" value={`${(promoSim.cannib * 100).toFixed(0)}%`} tone="warning" />
                <SimStat label="Orders (24h)" value={formatNumber(promoSim.orders, { compact: true })} tone="info" />
                <SimStat label="Net ₱ Impact" value={formatPeso(promoSim.netImpact, { compact: true })} tone={promoSim.netImpact > 0 ? "success" : "critical"} />
              </div>

              <div className="p-3 bg-surface-2 border border-border rounded-sm flex items-start gap-2.5 text-[12px]">
                <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
                <div className="text-fg/90 leading-relaxed flex-1">
                  <strong className="text-primary">AI:</strong> At {discountAdj.toFixed(0)}% discount, projected {(promoSim.uplift * 100).toFixed(0)}% uplift on {activePromo.skus} SKUs, but {(promoSim.cannib * 100).toFixed(0)}% volume is cannibalized from adjacent SKUs. Margin compression {(promoSim.marginImpact * 100).toFixed(1)}pp. {discountAdj > activePromo.discount * 100 + 5 ? "Trim discount by 3–5pp for better margin without losing uplift." : "Discount is well-calibrated."}
                  <button
                    data-testid="ask-copilot-promo"
                    onClick={() => askCopilot(
                      `As Merchandising Manager, I'm planning the "${activePromo.name}" promotion (week ${activePromo.week}, channel ${activePromo.channel}, ${activePromo.skus} SKUs). Current sim inputs: discount ${discountAdj.toFixed(0)}% (baseline ${(activePromo.discount * 100).toFixed(0)}%), projected uplift ${promoSim.uplift.toFixed(2)}× → ${formatNumber(promoSim.orders)} orders (24h window), gross revenue ${formatPeso(promoSim.grossRev, { compact: true })}, cannibalization ${(promoSim.cannib * 100).toFixed(0)}%, net ₱ impact ${formatPeso(promoSim.netImpact, { compact: true })}, margin compression ${(promoSim.marginImpact * 100).toFixed(1)}pp. The category elasticities are: Auto ${PRICE_ELASTICITY.find(e => e.categoryId === "AUTO").elasticity}, HVAC ${PRICE_ELASTICITY.find(e => e.categoryId === "HVAC").elasticity}, Refrig ${PRICE_ELASTICITY.find(e => e.categoryId === "REFR").elasticity}, Consumables ${PRICE_ELASTICITY.find(e => e.categoryId === "CONS").elasticity}. Advise me: (1) is ${discountAdj.toFixed(0)}% the optimal discount or should I revise? (2) which SKU categories should I prioritise/exclude given their elasticity? (3) what pre-position inventory and which branches need stock-up to keep fill-rate >95% during the spike? (4) how do I mitigate the ${(promoSim.cannib * 100).toFixed(0)}% cannibalization?`
                    )}
                    className="ml-2 inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 hover:underline font-medium align-baseline whitespace-nowrap"
                  >
                    <MessageSquare size={11} /> Ask Copilot to deep-dive →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Tile>

        {/* Seasonality & Weather */}
        <Tile className="col-span-12 lg:col-span-5" testid="seasonality-weather"
              title="Seasonality × Weather Correlation">
          <div style={{ height: 240 }}>
            <ResponsiveContainer>
              <ComposedChart data={SEASONALITY_WEATHER} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="month" tick={tickStyle} stroke={C.borderStrong} />
                <YAxis yAxisId="left" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${v.toFixed(1)}×`} />
                <YAxis yAxisId="right" orientation="right" tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `${v}°`} />
                <Tooltip content={<TT />} />
                <Bar yAxisId="left" dataKey="autoDemand" name="Auto Demand" fill={C.chartAlpha(1, 0.7)} isAnimationActive={false} />
                <Bar yAxisId="left" dataKey="hvacDemand" name="HVAC Demand" fill={C.chartAlpha(2, 0.7)} isAnimationActive={false} />
                <Line yAxisId="right" type="monotone" dataKey="heatIndex" name="Heat Index" stroke={C.critical} strokeWidth={2.2} dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-muted mt-1 leading-relaxed">
            <strong className="text-fg">Correlation:</strong> Heat-index leads HVAC + Auto-AC demand by ~3 weeks. R²=0.78 across last 4 years. Typhoon-active months (Jul–Oct) lift refrigeration demand (cold-chain disruption).
          </div>
        </Tile>

        {/* Brand × Category margin contribution */}
        <Tile className="col-span-12 lg:col-span-7" testid="brand-category-margin" title="Brand × Category Margin Contribution">
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={["1Rotary", "ROTEK", "North ROTEK"].map(b => {
                const rows = BRAND_CATEGORY_MARGIN.filter(r => r.brand === b);
                const out = { brand: b };
                rows.forEach(r => { out[r.category] = r.margin; });
                return out;
              })} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid stroke={C.border} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="brand" tick={{ ...tickStyle, fontFamily: "IBM Plex Sans" }} stroke={C.borderStrong} />
                <YAxis tick={tickStyle} stroke={C.borderStrong} tickFormatter={v => `₱${(v / 1e6).toFixed(0)}M`} />
                <Tooltip content={<TT formatter={v => formatPeso(v, { compact: true })} />} cursor={{ fill: C.surface2 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {CATEGORIES.map((c, i) => (
                  <Bar key={c.name} dataKey={c.name} stackId="m" fill={C.chart[i % 5]} radius={i === CATEGORIES.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]} isAnimationActive={false} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tile>

        {/* NPI Tracker */}
        <Tile className="col-span-12" testid="npi-tracker" title="New Product Introduction (NPI) Tracker"
              action={<Badge status="info">{NPI_TRACKER.filter(n => n.status === "on-track").length} on-track · {NPI_TRACKER.filter(n => n.status === "outperforming").length} outperforming</Badge>}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-2 px-3 text-left">ID</th>
                <th className="py-2 px-3 text-left">SKU</th>
                <th className="py-2 px-3 text-left">Stage</th>
                <th className="py-2 px-3 text-right">Branches</th>
                <th className="py-2 px-3 text-right">Weeks Live</th>
                <th className="py-2 px-3 text-right">Units</th>
                <th className="py-2 px-3 text-right">Target</th>
                <th className="py-2 px-3 text-right">Progress</th>
                <th className="py-2 px-3 text-right">GM</th>
                <th className="py-2 px-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="font-mono-tabular">
              {NPI_TRACKER.map(n => {
                const prog = n.target > 0 ? n.units / n.target : 0;
                return (
                  <tr key={n.id} className="border-b border-border hover:bg-surface-2" data-testid={`npi-${n.id}`}>
                    <td className="py-2 px-3 text-muted">{n.id}</td>
                    <td className="py-2 px-3 font-sans font-medium">{n.sku}</td>
                    <td className="py-2 px-3 font-sans"><Badge status={n.stage === "Scale" ? "success" : n.stage === "Pilot" ? "info" : "neutral"}>{n.stage}</Badge></td>
                    <td className="py-2 px-3 text-right">{n.branches}</td>
                    <td className="py-2 px-3 text-right">{n.weeks}</td>
                    <td className="py-2 px-3 text-right">{formatNumber(n.units)}</td>
                    <td className="py-2 px-3 text-right text-muted">{formatNumber(n.target)}</td>
                    <td className="py-2 px-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-surface-2 rounded-sm overflow-hidden">
                          <div className={cn("h-full", prog >= 1 ? "bg-success" : prog >= 0.7 ? "bg-info" : prog >= 0.4 ? "bg-warning" : "bg-critical")}
                               style={{ width: `${Math.min(100, prog * 100)}%` }} />
                        </div>
                        <span className="text-[10px]">{(prog * 100).toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">{n.gm > 0 ? `${(n.gm * 100).toFixed(0)}%` : "—"}</td>
                    <td className="py-2 px-3 font-sans">
                      <Badge status={n.status === "outperforming" ? "success" : n.status === "on-track" ? "info" : n.status === "at-risk" ? "warning" : n.status === "blocked" ? "critical" : "neutral"}>
                        {n.status}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tile>
      </div>
    </div>
  );
}

function SimStat({ label, value, tone }) {
  const map = { success: "text-success border-success/20", warning: "text-warning border-warning/30", critical: "text-critical border-critical/30", info: "text-info border-info/30" };
  return (
    <div className={cn("p-3 border rounded-sm bg-surface-2/40", map[tone] || "border-border")}>
      <div className="kpi-label">{label}</div>
      <div className={cn("text-xl font-display font-semibold mt-1", map[tone]?.split(" ")[0])}>{value}</div>
    </div>
  );
}

function Agg({ label, value, status }) {
  return (
    <div className="px-4 py-3">
      <div className="kpi-label flex items-center gap-2">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-2xl mt-1.5">{value}</div>
    </div>
  );
}
